import { useKioskStore } from '@/store/kiosk-store';

const API_BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';

function resolveUrl(path: string): string {
  if (path.startsWith('http')) return path;
  return API_BASE ? `${API_BASE.replace(/\/$/, '')}${path}` : path;
}

export class AuthError extends Error {
  readonly status = 401;
  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'AuthError';
  }
}

export class NotFoundError extends Error {
  readonly status = 404;
  constructor(message = 'Not found') {
    super(message);
    this.name = 'NotFoundError';
  }
}

/** Any other non-OK response; `status` lets callers tell 409, 410 and 5xx apart. */
export class HttpError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

export class NetworkError extends Error {
  readonly status = 0;
  constructor(message = 'Network error') {
    super(message);
    this.name = 'NetworkError';
  }
}

function buildHeaders(jwt: string | null): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  return headers;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new AuthError();
  if (res.status === 404) throw new NotFoundError();
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new HttpError(res.status, `HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export class ApiClient {
  async get<T>(path: string): Promise<T> {
    const jwt = useKioskStore.getState().jwt;
    try {
      const res = await fetch(resolveUrl(path), {
        method: 'GET',
        headers: buildHeaders(jwt),
      });
      return handleResponse<T>(res);
    } catch (err) {
      if (err instanceof AuthError || err instanceof NotFoundError) throw err;
      throw new NetworkError((err as Error).message);
    }
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    const jwt = useKioskStore.getState().jwt;
    try {
      const res = await fetch(resolveUrl(path), {
        method: 'POST',
        headers: buildHeaders(jwt),
        body: JSON.stringify(body),
      });
      return handleResponse<T>(res);
    } catch (err) {
      if (err instanceof AuthError || err instanceof NotFoundError) throw err;
      throw new NetworkError((err as Error).message);
    }
  }
}
